<template>
  <div :class="$style.container">
    <!-- Иконка для варианта тарифа -->
    <div :class="$style.preview">
      <base-icon
        v-p-t="4"
        v-color:black-2
        :size="16"
        name="arrows_arrow-right"
      />
    </div>
    <!-- Блок контента варианта тарифа -->
    <div :class="$style.content">
      <div v-m-b="6">
        <!-- Сумма за один билет и множитель за количество билетов (опционально) -->
        <div
          v-if="ticket?.code"
          v-m-b="6"
          :class="$style.amount"
        >
          <!-- Сумма за один билет -->
          <span
            v-text="displayTicketAmount"
          ></span>
          <!-- Множитель для количества билетов -->
          <span
            v-m-l="10"
            v-f-size="'12px'"
            v-opacity=".5"
            v-text="`x ${ticket.count}`"
          />
        </div>
        <!-- Сумма, указанная в выбранном варианте тарифа -->
        <div
          v-else
          v-m-b="6"
          :class="$style.amount"
          v-text="displayAmount"
        ></div>
        <!-- Название варианта тарифа (опционально) -->
        <div
          v-if="tariff?.name"
          v-m-b="6"
          v-f-weight="700"
          v-text="tariff.name"
        ></div>
        <!-- Описание настроек подписки (опционально) -->
        <template v-if="tariff?.subscription">
          <!-- Информация об оплате без подписки (опционально) -->
          <div
            v-if="tariff.subscription?.oneTimePayment"
            v-m-b="6"
            v-text="$t('paid.without.subscription')"
          ></div>
          <template v-else>
            <!-- Для подписки доступен пробный период (опционально) -->
            <div
              v-if="displaySubscriptionTrialDays"
              v-m-b="6"
              :class="$style.trial"
              v-text="displaySubscriptionTrialDays"
            ></div>
            <!-- Периодичность списания по подписке (отсутствует при выставлении счета через api) -->
            <div
              v-if="displaySubscriptionPer"
              v-m-b="6"
              v-text="displaySubscriptionPer"
            ></div>
          </template>
        </template>
      </div>
      <!-- Информация о промокоде (опционально) -->
      <applied-promo-code-details
        v-if="promoCode"
        v-m-b="6"
        :promo-code="promoCode"
      />
      <!-- Информация о вознаграждении за транзакцию (опционально) -->
      <applied-transaction-tips
        v-else-if="transactionTips"
        v-m-b="6"
        :variant="transactionTips"
      />
      <!-- Описание варианта тарифа (опционально) -->
      <template v-if="tariff?.description">
        <base-icon
          v-color:violet
          v-cursor:pointer
          v-absolute
          v-top="0"
          v-right="0"
          name="notifications_info"
          @click="isShownDescription = !isShownDescription"
        />
        <div
          v-if="isShownDescription"
          v-m-b="6"
          :class="$style.description"
          v-text="tariff.description"
        ></div>
      </template>
      <!-- Ссылка на купленный билет (опционально) -->
      <div
        v-if="ticket?.link"
        :class="$style.link"
      >
        <base-icon
          v-color:black
          v-m-r="5"
          :size="16"
          name="shopping_coupon"
        />
        <a
          v-color:text
          v-text-decoration:underline
          :href="ticket.link"
          target="_blank"
          v-text="$t('bill.ticket')"
        ></a>
      </div>
      <!-- Ссылка на цифровой контент (опционально) -->
      <div
        v-else-if="contentUrl"
        :class="$style.url"
      >
        <base-icon
          v-color:black
          v-m-r="5"
          :size="16"
          name="basic_paperclip"
        />
        <a
          v-color:text
          v-text-decoration:underline
          :href="contentUrl"
          target="_blank"
          v-text="$t('realised.content')"
        ></a>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount } from 'lib/utils/amount';
import { getUrlFromIframe } from 'lib/utils/url';

import TransactionService from 'services/transaction';

import AppliedPromoCodeDetails from 'molecules/transaction/details/AppliedPromoCodeDetails';
import AppliedTransactionTips from 'molecules/transaction/details/AppliedTransactionTips';

import { PERIODICITY_TEXT_MAPS } from 'definitions/subscription';

export default {
  name: 'PurchaseBillTariff',
  components: {
    AppliedPromoCodeDetails,
    AppliedTransactionTips,
  },
  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isShownDescription: false,
    };
  },
  computed: {
    displayAmount() {
      const { billAmount, currency } = this.transaction;

      if (this.$validate.isNull(billAmount)) {
        return this.$t('any.amount');
      }

      return formatAmount(billAmount, currency);
    },
    displayTicketAmount() {
      const { billAmount } = this.transaction;

      if (this.$validate.isNull(billAmount)) {
        return this.$t('any.amount');
      }

      if (this.$validate.isNull(this.ticket)) {
        return null;
      }

      return formatAmount(this.ticket?.amount, this.ticket?.currency);
    },
    displaySubscriptionPer() {
      const isValidSubscriptionPer = this.$validate
        .isObjectWithFields(PERIODICITY_TEXT_MAPS, [this.tariff?.subscription.per]);

      if (!isValidSubscriptionPer) {
        return null;
      }

      return this.$t(PERIODICITY_TEXT_MAPS[this.tariff.subscription.per]);
    },
    contentUrl() {
      return getUrlFromIframe(this.tariff?.contentUrl) || null;
    },
    tariff() {
      return TransactionService.getPurchaseBillTariff(this.transaction);
    },
    promoCode() {
      return TransactionService.getPromoCode(this.transaction);
    },
    transactionTips() {
      return TransactionService.getTransactionTips(this.transaction);
    },
    ticket() {
      return TransactionService.getTicketVariant(this.transaction);
    },
    displaySubscriptionTrialDays() {
      if (this.$validate.isNotNumber(this.tariff?.subscription?.trialDays)) {
        return null;
      }

      return this.$t('bill.subscription.details.trial.days', {
        days: this.$tc('pluralize.days', this.tariff.subscription.trialDays),
      });
    },
  },
};
</script>

<style lang="sass" module>
  .container
    +flexbox
    word-break: break-all

  .preview
    +flexbox
    +align-self(flex-start)
    flex: none
    margin-right: 26px

  .content
    +flex-grow
    +font-type('regular', 12, 18)
    position: relative

  .amount
    +font-type('medium', 18, 24)
    color: var(--color-black-2)

  .url
    +font-type('medium', 14, 20)
    +flexbox

  .link
    +font-type('regular', 12, 18)
    +flexbox

  .text
    +font-type('regular', 12, 18)
    color: var(--color-black-2)

    strong
      +font-weight('bold')

  .description
    display: inline-block
    padding: 10px
    background: rgba(40, 41, 61, 0.1)
    border-radius: 10px 0px 10px 10px

  .trial
    display: inline-block
    padding: 2px 7px
    background: rgba(40, 41, 61, 0.1)
    border-radius: 20px
</style>
